import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import ChooseVerificationStep from '@/components/page/emongoliaOtp/step1'
import FaceKycStep from '@/components/page/emongoliaOtp/faceKycStep'
import Loader from '@/components/ui/loader'
import RegisterStep from '@/components/page/emongoliaOtp/step2'
import Step3 from '@/components/page/emongoliaOtp/step3'
import OtpStep from '@/components/page/emongoliaOtp/step4'
import { useLanguage } from '@/context/LanguageContext'
import { useToast } from '@/context/ToastContext'
import { validate } from '@/services/mobi-api/confirmation'
import useIntl from '@/utils/intl'
import { normalizeTrackingText, trackNewNumberEvent } from '@/utils/newNumberFlowTracking'
import React, { FunctionComponent, useEffect, useState } from 'react'

type Method = 'emongolia' | 'kyc'
type Channel = 'sms' | 'email' | 'app'
type Stage = 'select' | 'register' | 'emongolia' | 'otp' | 'faceKyc'

interface Props {
  instanceId?: string
  initialRegno?: string
  onVerified: (payload: { instanceId: string, regno: string }) => void
}

const VerificationSection: FunctionComponent<Props> = ({
  instanceId,
  initialRegno,
  onVerified
}) => {
  const { translate } = useIntl()
  const { locale } = useLanguage()
  const { showError } = useToast()
  const [stage, setStage] = useState<Stage>('select')
  const [method, setMethod] = useState<Method>('emongolia')
  const [regno, setRegno] = useState(initialRegno ?? '')
  const [selectedChannel, setSelectedChannel] = useState<Channel>('app')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setRegno(initialRegno ?? '')
  }, [initialRegno])

  const goToChooseMethod = (): void => setStage('select')

  const handleCardClick = (selectedMethod?: Method): void => {
    if (loading) return

    const resolvedMethod = selectedMethod ?? 'emongolia'
    setMethod(resolvedMethod)

    if (regno.trim() === '') {
      setStage('register')
      return
    }

    if (resolvedMethod === 'kyc') {
      setStage('faceKyc')
      return
    }

    setStage('emongolia')
  }

  const handleOtpVerified = async (verifiedRegno: string): Promise<void> => {
    if (instanceId == null || instanceId.trim() === '') {
      showError(translate('request.failed'))
      return
    }

    try {
      setLoading(true)
      const validateResponse = await validate({
        instanceId,
        regno: verifiedRegno
      })

      if (
        validateResponse?._id != null &&
        validateResponse._id !== '' &&
        validateResponse?.action != null &&
        validateResponse.action !== ''
      ) {
        onVerified({
          instanceId: validateResponse._id,
          regno: verifiedRegno
        })

        trackNewNumberEvent('verification_success', {
          step_name: 'registration_info',
          sub_step_name: 'verification',
          method: 'emongolia_notification'
        })
        return
      }

      const message =
        locale === 'en'
          ? validateResponse?.cause?.messageEn ??
            validateResponse?.error?.messageEn ??
            translate('request.failed')
          : validateResponse?.cause?.messageMn ??
            validateResponse?.error?.messageMn ??
            translate('request.failed')

      trackNewNumberEvent('step_error', {
        step_name: 'registration_info',
        sub_step_name: 'verification',
        error_type: normalizeTrackingText(message)
      })
      showError(message)
    } catch (error) {
      trackNewNumberEvent('step_error', {
        step_name: 'registration_info',
        sub_step_name: 'verification',
        error_type: 'validate_verification_failed'
      })
      console.error(error)
      showError(translate('request.failed'))
    } finally {
      setLoading(false)
    }
  }

  const handleRegisterContinue = ({ regno }: { regno: string }): void => {
    setRegno(regno)

    if (method === 'kyc') {
      setStage('faceKyc')
      return
    }

    setStage('emongolia')
  }

  const handleChannelSelected = (channel: Channel): void => {
    setSelectedChannel(channel)
    setStage('otp')
  }

  if (stage === 'register') {
    return (
      <div className='rounded-xl bg-surfaceMid p-6 md:p-8'>
        <RegisterStep
          instanceId={instanceId}
          onContinue={({ regno }) => handleRegisterContinue({ regno })}
        />
        <div className='mt-4'>
          <DarkButton type='soft' onClick={goToChooseMethod} disabled={loading}>
            {translate('back')}
          </DarkButton>
        </div>
      </div>
    )
  }

  if (stage === 'faceKyc') {
    return (
      <FaceKycStep
        regno={regno}
        instanceId={instanceId}
        onContinue={() => onVerified({ instanceId: instanceId ?? '', regno })}
        onBackToChoose={goToChooseMethod}
      />
    )
  }

  if (stage === 'otp') {
    return (
      <OtpStep
        regno={regno}
        channel={selectedChannel}
        onContinue={({ regno }) => {
          handleOtpVerified(regno).catch(() => {})
        }}
      />
    )
  }

  if (stage === 'emongolia') {
    return (
      <div className='rounded-xl bg-surfaceMid p-6 md:p-8'>
        <Step3 regno={regno} onContinue={({ channel }) => handleChannelSelected(channel)} />
        <div className='mt-4'>
          <DarkButton type='soft' onClick={() => setStage('register')} disabled={loading}>
            {translate('back')}
          </DarkButton>
        </div>
      </div>
    )
  }

  return (
    <div className='rounded-xl bg-surfaceMid p-6 md:p-8 relative' aria-busy={loading}>
      {loading && (
        <div className='absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-black/20 backdrop-blur-[2px]'>
          <Loader />
        </div>
      )}

      <div className='flex flex-col gap-4'>
        <ChooseVerificationStep
          service='newNumber'
          onChooseEmongolia={(selectedMethod) => handleCardClick(selectedMethod)}
        />
      </div>
    </div>
  )
}

export default VerificationSection
